import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Eye, EyeOff, Radar, ShieldCheck, ScrollText } from 'lucide-react';
import Logo from '@/components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Login() {
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [ssoConfig, setSsoConfig] = useState(null);
  const [orgRequired, setOrgRequired] = useState(false);
  const [orgId, setOrgId] = useState('');

  useEffect(() => {
    // Check for SSO callback code in URL and exchange for tokens
    const params = new URLSearchParams(window.location.search);
    const ssoCode = params.get('sso_code');
    if (ssoCode) {
      fetch(`${BACKEND_URL}/api/v1/auth/sso/exchange`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: ssoCode }),
      })
        .then(r => r.json())
        .then(data => {
          if (data.access_token) {
            localStorage.setItem('asm_token', data.access_token);
            if (data.refresh_token) localStorage.setItem('asm_refresh', data.refresh_token);

            // Decode JWT to get user info for localStorage
            try {
              const payload = JSON.parse(atob(data.access_token.split('.')[1]));
              const userData = {
                id: payload.sub,
                role: payload.role,
                org_id: payload.org_id,
                email: payload.email || '',
              };
              localStorage.setItem('asm_user', JSON.stringify(userData));
            } catch (e) {
              console.error('Failed to decode SSO token:', e);
            }

            window.history.replaceState({}, '', '/login');
            window.location.href = '/';
          }
        })
        .catch(err => console.error('SSO exchange failed:', err));
      return;
    }
    // Fetch SSO config
    fetch(`${BACKEND_URL}/api/v1/auth/sso/config`)
      .then(r => r.json())
      .then(data => setSsoConfig(data))
      .catch(() => {});
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      // If org_id is required and provided, pass it in the login call
      if (orgRequired && orgId) {
        const res = await api.post('/auth/login', { email, password, org_id: orgId });
        const { access_token, refresh_token: rt, user: userData } = res.data;
        localStorage.setItem('asm_token', access_token);
        localStorage.setItem('asm_refresh', rt);
        localStorage.setItem('asm_user', JSON.stringify(userData));
        navigate('/');
      } else {
        await login(email, password);
        navigate('/');
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Invalid credentials';
      if (detail.includes('multiple organizations')) {
        setOrgRequired(true);
        setError('This email exists in multiple organizations. Enter your Organization ID below.');
      } else {
        setError(detail);
      }
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-900 md:flex-row">
      {/* Left Branding Panel */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden border-r border-slate-800 bg-slate-900 md:flex p-12 lg:p-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.18),transparent_30%),radial-gradient(circle_at_bottom_left,rgba(139,92,246,0.14),transparent_30%)]" />
        <div className="absolute inset-0 opacity-20 [background-image:linear-gradient(rgba(148,163,184,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.08)_1px,transparent_1px)] [background-size:48px_48px]" />
        <div className="relative z-10">
          <Logo size="lg" />
          <div className="space-y-6">
            <p className="mt-12 text-[11px] uppercase tracking-[0.32em] text-blue-300">Adaptive Monitoring &amp; Response</p>
            <h2 className="text-5xl font-extrabold text-white leading-tight">
              Workforce telemetry for
              <br />
              insider risk operations.
            </h2>
            <p className="text-slate-400 text-lg max-w-md">
              Self-hosted employee monitoring and insider threat detection for security teams operating across distributed workforces.
            </p>
          </div>
          <div className="mt-16 space-y-8">
            <div className="flex items-start gap-4">
              <Radar className="w-5 h-5 mt-1 text-blue-400" />
              <div>
                <h4 className="text-white font-semibold">Continuous endpoint visibility</h4>
                <p className="text-slate-500 text-sm">Monitor work-from-home activity, telemetry, and device posture in real time.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <ShieldCheck className="w-5 h-5 mt-1 text-purple-400" />
              <div>
                <h4 className="text-white font-semibold">Policy-driven risk detection</h4>
                <p className="text-slate-500 text-sm">Flag productivity anomalies, insider threat indicators, and policy violations with audit-ready evidence.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <ScrollText className="w-5 h-5 mt-1 text-blue-400" />
              <div>
                <h4 className="text-white font-semibold">Compliance-first deployment</h4>
                <p className="text-slate-500 text-sm">Transparent consent workflows and self-hosted control for regulated environments.</p>
              </div>
            </div>
          </div>
        </div>
        <div className="relative z-10 flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-[0.24em] text-slate-500">Control Plane</span>
          <span className="flex h-2 w-2 rounded-full bg-blue-400 animate-pulse" />
          <span className="text-[11px] uppercase tracking-[0.24em] text-blue-300">Ready</span>
        </div>
      </div>

      {/* Right Login Form */}
      <div className="relative flex flex-1 flex-col justify-center overflow-hidden bg-slate-900 px-6 py-12 lg:px-24">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(59,130,246,0.08),transparent_35%),radial-gradient(circle_at_bottom,rgba(139,92,246,0.08),transparent_35%)]" />
        <div className="mx-auto w-full max-w-md">
          <div className="relative z-10">
            <div className="mb-8 flex flex-col items-center text-center">
              <Logo size="lg" />
              <p className="mt-4 text-sm text-slate-400">
                Insider Threat Detection &amp; Productivity Monitoring
              </p>
            </div>
            <div className="rounded-2xl border border-slate-800 bg-slate-800/80 p-8 shadow-2xl shadow-slate-950/40 backdrop-blur-xl">
              <div className="mb-8">
                <p className="text-[11px] uppercase tracking-[0.28em] text-blue-300">Administrator Access</p>
                <h2 className="mt-3 text-3xl font-bold tracking-tight text-white" data-testid="login-title">Sign in to ASM</h2>
                <p className="mt-2 text-sm text-slate-400">Secure access to monitoring, alerts, evidence, and policy controls.</p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
                <div>
                  <label className="block text-sm font-medium text-slate-300" htmlFor="email">Email Address</label>
                  <input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@company.com"
                    className="mt-1 block w-full rounded-xl border border-slate-600 bg-slate-700/80 py-4 px-4 text-white shadow-sm placeholder:text-slate-500 focus:ring-2 focus:ring-inset focus:ring-blue-500 text-sm"
                    data-testid="login-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300" htmlFor="password">Password</label>
                  <div className="mt-1 relative">
                    <input
                      id="password"
                      type={showPw ? 'text' : 'password'}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      className="block w-full rounded-xl border border-slate-600 bg-slate-700/80 py-4 px-4 pr-12 text-white shadow-sm placeholder:text-slate-500 focus:ring-2 focus:ring-inset focus:ring-blue-500 text-sm"
                      data-testid="login-password-input"
                    />
                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-500 hover:text-slate-300">
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="rounded-xl bg-red-500/10 p-4 border border-red-500/20" data-testid="login-error">
                    <p className="text-sm text-red-500 font-medium">{error}</p>
                  </div>
                )}

                {orgRequired && (
                  <div>
                    <label className="block text-sm font-medium text-slate-300" htmlFor="orgId">Organization ID</label>
                    <input id="orgId" type="text" value={orgId} onChange={(e) => setOrgId(e.target.value)}
                      placeholder="Enter your organization ID"
                      className="mt-1 block w-full rounded-xl border border-slate-600 bg-slate-700/80 py-4 px-4 text-white shadow-sm placeholder:text-slate-500 focus:ring-2 focus:ring-inset focus:ring-blue-500 text-sm"
                      data-testid="login-org-input" />
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="flex w-full justify-center rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 px-3 py-4 text-sm font-bold text-white shadow-lg shadow-blue-950/30 hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 transition disabled:opacity-50"
                  data-testid="login-submit-btn"
                >
                  {loading ? 'Logging in...' : 'Log In'}
                </button>
              </form>
              {ssoConfig?.enabled && (
                <div className="mt-4">
                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-700" /></div>
                    <div className="relative flex justify-center text-xs"><span className="bg-slate-800 px-2 text-slate-500">or</span></div>
                  </div>
                  <a href={`${BACKEND_URL}${ssoConfig.login_url}`}
                    className="flex w-full justify-center rounded-xl border border-slate-700 bg-slate-900 px-3 py-4 text-sm font-bold text-white shadow-sm hover:bg-slate-700 transition-colors"
                    data-testid="sso-login-btn">
                    Sign in with {ssoConfig.provider || 'SSO'}
                  </a>
                </div>
              )}
            </div>
            <footer className="mt-6">
              <p className="text-center text-xs text-slate-600">
                Self-hosted | GDPR-Native | Transparent Consent
              </p>
            </footer>
          </div>
        </div>
      </div>
    </div>
  );
}
